type Delivery<T> = {
  controller: AbortController;
  consumers: Set<symbol>;
  promise: Promise<T | null>;
  settled: boolean;
};

const pending = new WeakMap<typeof fetch, Map<string, Delivery<unknown>>>();

/** Share only an active read; settled private image bytes are never cached. */
export function coalesceImageDelivery<T>(
  fetcher: typeof fetch,
  key: string,
  signal: AbortSignal,
  load: (signal: AbortSignal) => Promise<T | null>,
): Promise<T | null> {
  if (signal.aborted) return Promise.resolve(null);
  let requests = pending.get(fetcher);
  if (!requests) {
    requests = new Map();
    pending.set(fetcher, requests);
  }
  let delivery = requests.get(key);
  if (!delivery) {
    const controller = new AbortController();
    delivery = {
      controller,
      consumers: new Set(),
      settled: false,
      promise: Promise.resolve().then(() =>
        controller.signal.aborted ? null : load(controller.signal),
      ),
    };
    requests.set(key, delivery);
    const created = delivery;
    const evict = () => {
      created.settled = true;
      if (requests.get(key) === created) requests.delete(key);
    };
    void delivery.promise.then(evict, evict);
  }
  // Each caller namespaces its key by image kind and uses one result type per key.
  const shared = delivery as Delivery<T>;
  return new Promise((resolve, reject) => {
    const consumer = Symbol();
    shared.consumers.add(consumer);
    let finished = false;
    const finish = (settle: () => void) => {
      if (finished) return;
      finished = true;
      signal.removeEventListener('abort', abort);
      shared.consumers.delete(consumer);
      settle();
    };
    const abort = () => {
      finish(() => resolve(null));
      if (!shared.settled && shared.consumers.size === 0) {
        if (requests.get(key) === shared) requests.delete(key);
        shared.controller.abort();
      }
    };
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) abort();
    void shared.promise.then(
      (blob) => finish(() => resolve(signal.aborted ? null : blob)),
      (error) => finish(() => reject(error)),
    );
  });
}
